import { useEffect, useMemo, useState } from 'react';
import {
  Truck, AlertTriangle, Users, Shield, Activity, Bell, ClipboardList, Receipt
} from 'lucide-react';
import { KpiCard } from '../../components/ui/Card';
import { Select } from '../../components/ui/Input';
import { RankingTable } from '../../components/drivers/RankingTable';
import { AlertsStatsPanel } from '../../components/dashboard/AlertsStatsPanel';
import { LogbookStatsPanel } from '../../components/dashboard/LogbookStatsPanel';
import { ExpensesStatsPanel } from '../../components/dashboard/ExpensesStatsPanel';
import { useVehicles } from '../../hooks/useVehicles';
import { useAlertsContext } from '../../context/AlertsContext';
import { useDrivers } from '../../hooks/useDrivers';
import { DATE_FILTERS, rangoDeFiltro, type DateFilterId } from '../../utils/dateFilters';

const PERIOD_LABELS: Record<DateFilterId, string> = {
  today: 'hoy',
  '7days': 'últimos 7 días',
  month: 'este mes',
  all: 'todo el histórico',
};

type StatsTab = 'alerts' | 'logbook' | 'expenses';

const STATS_TABS: { id: StatsTab; label: string; icon: React.ReactNode }[] = [
  { id: 'alerts', label: 'Alertas', icon: <Bell size={15} /> },
  { id: 'logbook', label: 'Novedades', icon: <ClipboardList size={15} /> },
  { id: 'expenses', label: 'Gastos', icon: <Receipt size={15} /> },
];

// FE-04: antes esta pantalla vivía enteramente de mockData.ts, con un
// "filtro temporal" (Hoy/7 días/Mes/30 días) que aplicaba un multiplicador
// inventado a los conteos. Ese multiplicador se retiró: con datos reales no
// hay nada honesto que multiplicar. Los KPIs muestran el estado actual, no
// una comparación contra un periodo anterior inventado.
//
// Las agregaciones (por día, categoría, vehículo…) viven en
// `utils/dashboardStats.ts`; cada pestaña es un panel en `components/dashboard/`.
export default function DashboardPage() {
  useEffect(() => { document.title = 'Dashboard | Paitron Radar'; }, []);

  const { vehicles } = useVehicles();
  const { alerts } = useAlertsContext();
  const { drivers } = useDrivers();

  // issue FE-24 historia 2: filtro de tiempo para las pestañas — los KPIs y el
  // ranking siguen siendo el estado ACTUAL de la flota, no algo que un rango
  // de fechas deba acotar.
  const [periodFilter, setPeriodFilter] = useState<DateFilterId>('month');
  const [tab, setTab] = useState<StatsTab>('alerts');
  const { from, to } = useMemo(() => rangoDeFiltro(periodFilter), [periodFilter]);
  const periodLabel = PERIOD_LABELS[periodFilter];

  const periodAlerts = useMemo(() => {
    if (!from || !to) return alerts; // 'all'
    const fromT = new Date(from).getTime();
    const toT = new Date(to).getTime();
    return alerts.filter(a => {
      const t = new Date(a.timestamp).getTime();
      return t >= fromT && t <= toT;
    });
  }, [alerts, from, to]);

  const activeVehicles = vehicles.filter(v => v.status !== 'offline').length;
  const criticalAlerts = alerts.filter(a => a.status === 'active' && a.severity === 'critical').length;

  // `Driver.score` sigue siendo opcional (FE-16) — el promedio y el conteo
  // de alto riesgo solo tienen sentido sobre los conductores que ya lo tienen.
  const scoredDrivers = drivers.filter(d => d.score);
  const avgScore = scoredDrivers.length > 0
    ? Math.round(scoredDrivers.reduce((acc, d) => acc + (d.score?.value ?? 0), 0) / scoredDrivers.length)
    : null;
  const highRiskDrivers = scoredDrivers.filter(d => d.score?.level === 'high').length;

  return (
    <div className="dashboard-page">
      {/* KPI Cards */}
      <section className="dashboard-page__kpis" aria-label="Indicadores clave">
        <KpiCard
          title="Vehículos activos"
          value={activeVehicles}
          subtitle={`de ${vehicles.length} total`}
          icon={<Truck size={22} />}
          color="info"
        />
        <KpiCard
          title="Alertas críticas activas"
          value={criticalAlerts}
          subtitle="Requieren atención inmediata"
          icon={<AlertTriangle size={22} />}
          color={criticalAlerts > 0 ? 'danger' : 'success'}
        />
        <KpiCard
          title="Score promedio de flota"
          value={avgScore ?? '—'}
          subtitle={avgScore !== null ? 'sobre 100 puntos' : `pendiente — 0 de ${drivers.length} conductores`}
          icon={<Activity size={22} />}
          color={avgScore === null ? 'default' : avgScore >= 75 ? 'success' : avgScore >= 45 ? 'warning' : 'danger'}
        />
        <KpiCard
          title="Conductores alto riesgo"
          value={scoredDrivers.length > 0 ? highRiskDrivers : '—'}
          subtitle={scoredDrivers.length > 0 ? `de ${scoredDrivers.length} con score` : 'sin score calculado todavía'}
          icon={<Shield size={22} />}
          color={highRiskDrivers > 0 ? 'warning' : 'success'}
        />
      </section>

      {/* Estadísticas por pestaña: un solo filtro de periodo para las tres,
          separado de los KPIs de arriba, que son el estado actual. */}
      <section aria-label="Estadísticas del periodo" className="dashboard-page__stats-section">
        <div className="dashboard-page__tabbar">
          <div className="reports-tabs" role="tablist" aria-label="Estadísticas">
            {STATS_TABS.map(t => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                id={`dashboard-tab-${t.id}`}
                className={`reports-tab ${tab === t.id ? 'reports-tab--active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>
          <div className="dashboard-page__period">
            <Select
              aria-label="Rango de tiempo de las estadísticas"
              value={periodFilter}
              onChange={e => setPeriodFilter(e.target.value as DateFilterId)}
              options={DATE_FILTERS.map(f => ({ value: f.id, label: f.label }))}
            />
          </div>
        </div>

        <div role="tabpanel" aria-labelledby={`dashboard-tab-${tab}`}>
          {tab === 'alerts' && (
            <AlertsStatsPanel alerts={periodAlerts} from={from} to={to} periodLabel={periodLabel} />
          )}
          {tab === 'logbook' && <LogbookStatsPanel from={from} to={to} periodLabel={periodLabel} />}
          {tab === 'expenses' && <ExpensesStatsPanel from={from} to={to} periodLabel={periodLabel} />}
        </div>
      </section>

      {/* Ranking */}
      <section className="dashboard-page__ranking">
        <div className="dashboard-page__section-header">
          <h2 className="dashboard-page__section-title">
            <Users size={18} />
            Ranking de riesgo — conductores
          </h2>
          <span className="dashboard-page__section-sub">Haz clic en los encabezados para ordenar</span>
        </div>
        <RankingTable drivers={drivers} />
      </section>
    </div>
  );
}
